"use client";

import { useEffect, useState } from "react";
import clsx from "clsx";

/**
 * The site's page heading — the 28/36/45/55px Avenir title behind the 5px red
 * accent bar that every tabbed section header uses.
 *
 * It exists because that class string was pasted into 15 places and the CSS for
 * its transition into two, so every tweak had to be repeated by hand and drifted:
 * one header ended up scanning its title in while another still cross-faded, and
 * a `letter-spacing` animation that had been removed from one stayed live in the
 * other, spreading its glyphs 23px on mount.
 *
 * ## The transition
 *
 * Changing `children` uncovers the new title left to right while the outgoing one
 * clears with a short fade underneath it, in the same grid cell, so it costs no
 * layout. Both run on `clip-path`, `opacity` and `transform` only — nothing that
 * re-lays out text mid-animation.
 *
 * The reveal used to be led by a bright red 3px edge sweeping ahead of the text,
 * locked to the clip boundary. It was dropped on request: the wipe alone carries
 * the change, and the red bar read as one decoration too many next to the accent
 * bar it travelled away from.
 *
 * Layouts keep the title mounted across their own routes, so a plain
 * `key={pathname}` remount is not enough: it can only cut, and on a shared layout
 * it does not even re-run. Holding the previous string in state is what makes a
 * transition possible at all.
 *
 * ## `mobileLines`
 *
 * Pass the line count of the LONGEST title this header can show, measured below
 * `sm`. The height is reserved so that picking that title does not drop the tab
 * row and everything under it — on the Products header that shift measured 0.0844
 * of CLS on its own. Titles were checked at 390px: Products, Services, Defend and
 * Careers each have one that wraps to two lines; OT Security, Resources, Company
 * and Contact all fit on one.
 */

/** Reserved height below `sm`, keyed by line count (mobile line-height is 36px). */
const MOBILE_RESERVE: Record<number, string> = {
  1: "",
  2: "min-h-[72px] sm:min-h-0",
  3: "min-h-[108px] sm:min-h-0",
};

/** Long enough to outlast the 140ms fade-out of the outgoing copy. */
const OUTGOING_MS = 200;

interface PageTitleProps {
  /** The heading text. Changing it runs the scan transition. */
  children: string;
  /** Spacing utilities — the one thing that legitimately differs per page. */
  className?: string;
  /** A couple of headings are drawn without the red bar in Figma. */
  accent?: boolean;
  /** Line count of the longest title this header can show, below `sm`. */
  mobileLines?: 1 | 2 | 3;
  /**
   * Whether the scan also runs on the very first render.
   *
   * Section headers above a tab row want it — the header announcing itself is
   * part of the page arriving. Static headings do not, especially the ones
   * wrapped in `ScrollReveal`: that already fades them in, and two entrances
   * stacked on one heading read as a stutter. It is also how the policy pages
   * stay motion-free, which was an explicit request.
   *
   * Independent of a later change to `children` — that always animates, so a
   * heading whose text can still change gets its transition either way.
   */
  animateOnMount?: boolean;
}

export default function PageTitle({
  children,
  className,
  accent = true,
  mobileLines = 1,
  animateOnMount = true,
}: PageTitleProps) {
  const [shown, setShown] = useState(children);
  const [outgoing, setOutgoing] = useState<string | null>(null);
  // Distinguishes "this title arrived by a change" from "this title was here on
  // mount", which is the only thing `animateOnMount` needs to gate.
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    if (children === shown) return;
    setOutgoing(shown);
    setShown(children);
    setChanged(true);
  }, [children, shown]);

  // Retiring the outgoing copy gets its own effect, keyed on `outgoing` alone.
  // Folded into the swap above it never fires: that effect depends on `shown`,
  // which it sets itself, so the re-run tears down its own timer and then returns
  // early, leaving the old title parked in the DOM indefinitely.
  useEffect(() => {
    if (outgoing === null) return;
    const timer = setTimeout(() => setOutgoing(null), OUTGOING_MS);
    return () => clearTimeout(timer);
  }, [outgoing]);

  const scanning = animateOnMount || changed;

  return (
    <div className={clsx(MOBILE_RESERVE[mobileLines], className)}>
      {/* The reservation sits on this wrapper rather than the h1 because the
          accent bar is the h1's own left border: a min-height on the h1 would
          stretch that bar past the text it marks, leaving it hanging into the
          empty second line.

          justify-items-start keeps each grid cell at its text width, which the
          reveal depends on: the clip's inset is a percentage of the cell, so a
          full-width cell would finish uncovering a short title like "Team" a
          third of the way in and then spend the rest of the animation on empty
          column. */}
      <h1
        className={clsx(
          "grid justify-items-start",
          "text-text-highlight font-bold",
          "text-[28px] sm:text-[36px] md:text-[45px] lg:text-[55px]",
          "leading-[36px] sm:leading-[48px] md:leading-[60px] lg:leading-[79px]",
          "tracking-[0px]",
          accent && "border-l-[5px] border-solid border-primary pl-4 md:pl-6"
        )}
      >
        {/* Re-keyed on the title so the reveal replays on every change. */}
        <span key={shown} className="col-start-1 row-start-1">
          <span className={clsx("block", scanning && "title-in")}>{shown}</span>
        </span>
        {outgoing && (
          /* aria-hidden: the accessible name is the incoming title alone — a
             screen reader must not read the heading twice. */
          <span aria-hidden className="col-start-1 row-start-1 title-out">
            {outgoing}
          </span>
        )}
      </h1>

      <style jsx>{`
        /* clip-path carries the reveal, so no text is re-laid out mid-animation.
           The insets are both percentages so they interpolate without mixing
           units, and the end state sits just past the right edge so the last
           glyph's antialiasing is never shaved in the final frames. */
        .title-in {
          animation: titleScan 320ms linear both;
        }

        @keyframes titleScan {
          from {
            clip-path: inset(0 100% 0 0);
          }
          to {
            clip-path: inset(0 -4% 0 0);
          }
        }

        /* Cleared well before the reveal finishes, so the old text is gone by the
           time the new one is a third uncovered and the two are never both
           strongly lit in the same frame. Letting it linger measured a combined
           opacity of 1.32, which reads as a double image rather than a swap. */
        .title-out {
          animation: titleOut 140ms ease-out both;
        }

        @keyframes titleOut {
          from {
            opacity: 1;
            transform: translateY(0);
          }
          to {
            opacity: 0;
            transform: translateY(4px);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          /* animation:none alone would strand the clip wherever the keyframe left
             it, so the finished state is restored explicitly. */
          .title-in {
            animation: none;
            clip-path: none;
          }

          /* Not animation:none — that would leave the outgoing copy at its natural
             opacity 1, stacking two titles in the one grid cell. */
          .title-out {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}
